import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AxiosResponse } from 'axios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useAxios from '../../hooks/useAxios'
import { useSettings } from '../../hooks/useSettings'
import { Destination } from '../../context/AxiosContext'
import FileContext, { FileReference } from '../../context/FileContext'
import { IFileDto } from '../../config/fileConfig'
import { DocumentOverviewItemDto, FolderType, IFolderOverviewItemDto, IRootOverviewItemDto } from '../../config/documentConfig'
import { FileDocumentAction } from '../../config/fileDocumentConfig'
import { DriveItemTreeSnapshot } from '../../context/fileDocumentStore'
import { nestDriveItems } from '../../components/Document/DocumentFlatTree'
import {
    driveItemListProperties,
    driveItemOverviewProperties,
    findFolderInTree,
    mapDriveItemToOverviewItemDto,
    resolveFolderInTree,
    uploadDocument,
} from '../../helpers/documentHelper'

export type TaskAttachments = {
    /** The dossier the task belongs to; undefined while it is being resolved or when no dossier is chosen. */
    selectedFile?: FileReference
    /** Metadata of the currently linked attachments; undefined means still loading. */
    attachments?: DocumentOverviewItemDto[]
    /** True once the attachments folder lookup finished and no folder was configured for this dossier. */
    attachmentFolderMissing: boolean
    /** Whether dropped files can be uploaded right now. */
    canUpload: boolean
    /** Uploads dropped files into the attachments folder and reports the new document ids. */
    uploadFiles: (files: File[]) => Promise<void>
    /** Items the document tree already has in hand, so ticking one never costs a request. */
    registerKnownItems: (items: DriveItem[]) => void
}

/** Rebuilds the nested overview shape the folder helpers expect from a flat cached tree. */
const asOverviewTree = (snapshot: DriveItemTreeSnapshot | undefined): IRootOverviewItemDto | undefined => {
    if (!snapshot?.rootFolder) {
        return undefined
    }
    const nested = nestDriveItems(snapshot.items) || []
    const children = nested.filter(item => item.parent?.id === snapshot.documentRootId)
    return mapDriveItemToOverviewItemDto({ ...snapshot.rootFolder, children } as DriveItem) as IRootOverviewItemDto
}

/**
 * The attachment side of the task form (HAM-4275): resolving the dossier, its attachments folder and the metadata of
 * the linked documents, plus uploading dropped files.
 *
 * Formik stays the single source of truth for `attachmentIds`; this hook only reports newly uploaded ids through
 * `onAttachmentsAdded` so the caller can write them there. The previous implementation kept a second copy in local
 * state, which drifted as soon as the document tree wrote to Formik directly.
 *
 * Everything that can be answered from what the app already knows is answered synchronously (HAM-4295): the dossier
 * comes from FileContext instead of `GET /file/{id}`, and the attachment metadata plus the attachments folder come
 * from the document tree the dossier screen already loaded. The requests are only the fallback.
 */
export default function useTaskAttachments(
    fileId: string | undefined,
    attachmentIds: string[] | undefined,
    onAttachmentsAdded: (ids: string[]) => void,
): TaskAttachments {
    const axios = useAxios()
    const dispatch = useDispatch()
    const { settings } = useSettings()
    const { getFile, documents } = useContext(FileContext)

    const [selectedFile, setSelectedFile] = useState<FileReference>(() => getFile?.(fileId))
    const [attachmentFolder, setAttachmentFolder] = useState<IFolderOverviewItemDto>()
    const [attachmentFolderLoaded, setAttachmentFolderLoaded] = useState<boolean>(false)
    const [attachments, setAttachments] = useState<DocumentOverviewItemDto[]>(fileId ? undefined : [])

    // Metadata the document tree handed us; keyed by id so a tick in the tree never needs a request.
    const knownItemsRef = useRef<Map<string, DocumentOverviewItemDto>>(new Map())

    // The dropzone captures its callback once, so the folder is read through a ref to avoid a stale value.
    const attachmentFolderRef = useRef<IFolderOverviewItemDto>(attachmentFolder)
    useEffect(() => {
        attachmentFolderRef.current = attachmentFolder
    }, [attachmentFolder])

    useEffect(() => {
        if (fileId === selectedFile?.id) {
            return
        }

        setAttachmentFolder(undefined)
        setAttachmentFolderLoaded(false)

        if (!fileId) {
            setSelectedFile(undefined)
            setAttachments([])
            return
        }

        // The dossier list is loaded eagerly and holds everything this form needs, so a fetch is only left for a
        // dossier that is not in it (archived, or the list has not arrived yet).
        const known = getFile?.(fileId)
        if (known) {
            setSelectedFile(known)
        } else {
            axios.get<IFileDto>(`/file/${fileId}`).then(result => setSelectedFile(result.data))
        }
    }, [fileId])

    useEffect(() => {
        if (!selectedFile) {
            return
        }

        const snapshot = documents?.getTree(selectedFile.documentRootId)
        const cachedTree = asOverviewTree(snapshot)
        const cachedFolder = cachedTree
            ? (findFolderInTree(cachedTree, FolderType.Attachments, settings?.folders) as IFolderOverviewItemDto)
            : undefined

        if (cachedFolder) {
            setAttachmentFolder(cachedFolder)
            setAttachmentFolderLoaded(true)
        } else {
            axios
                .get<DriveItem>(`/items/${selectedFile.documentRootId}`, {
                    baseURL: Destination.Documents,
                    params: { $select: driveItemListProperties.join(','), $expand: 'children' },
                    headers: { SuppressErrors: 'true' },
                })
                .then(response => mapDriveItemToOverviewItemDto(response.data) as IRootOverviewItemDto)
                // The fetched tree is only one level deep; resolve fetches nested levels on demand.
                .then(response => resolveFolderInTree(axios, response, FolderType.Attachments, settings?.folders))
                .then(response => setAttachmentFolder(response))
                .finally(() => setAttachmentFolderLoaded(true))
        }

        // Anything the cached tree or the document tree already carries needs no request of its own.
        for (const item of snapshot?.items || []) {
            if (!knownItemsRef.current.has(item.id)) {
                knownItemsRef.current.set(item.id, mapDriveItemToOverviewItemDto(item))
            }
        }

        const ids = attachmentIds || []
        const resolved = ids.map(id => knownItemsRef.current.get(id)).filter(Boolean)
        const missing = ids.filter(id => !knownItemsRef.current.has(id))

        if (missing.length === 0) {
            setAttachments(resolved)
            return
        }

        setAttachments(undefined)
        Promise.all(
            missing.map(attachmentId =>
                axios
                    .get<DriveItem>(`/items/${attachmentId}`, {
                        baseURL: Destination.Documents,
                        params: { $select: driveItemOverviewProperties.join(',') },
                        headers: { SuppressErrors: 'true' },
                    })
                    .then(response => (response.data?.id ? mapDriveItemToOverviewItemDto(response.data) : undefined)),
            ),
        ).then(loaded => setAttachments([...resolved, ...loaded.filter(Boolean)]))
    }, [selectedFile?.id])

    const registerKnownItems = (items: DriveItem[]) => {
        for (const item of items || []) {
            if (item?.id && !knownItemsRef.current.has(item.id)) {
                knownItemsRef.current.set(item.id, mapDriveItemToOverviewItemDto(item))
            }
        }
        // Fill in metadata for anything that is linked but was still unknown, without a request.
        setAttachments(previous =>
            previous === undefined
                ? previous
                : Array.from(
                      new Map(
                          [...previous, ...(attachmentIds || []).map(id => knownItemsRef.current.get(id)).filter(Boolean)].map(item => [
                              item.id,
                              item,
                          ]),
                      ).values(),
                  ),
        )
    }

    const uploadFiles = (files: File[]): Promise<void> => {
        const folderId = attachmentFolderRef.current?.id
        if (!folderId) {
            return Promise.resolve()
        }

        return Promise.all((files || []).map(file => uploadDocument(axios, dispatch, folderId, file)))
            .then((driveItems: AxiosResponse<DriveItem>[]) => {
                const uploaded = driveItems.map(driveItem => mapDriveItemToOverviewItemDto(driveItem.data))
                setAttachments(previous => Array.from(new Map([...(previous || []), ...uploaded].map(item => [item.id, item])).values()))
                onAttachmentsAdded(uploaded.map(item => item.id).filter(Boolean))
            })
            .then(() => {
                // The dossier's document overview and the cached tree both no longer match SharePoint.
                documents?.invalidate(selectedFile?.documentRootId)
                dispatch({ type: FileDocumentAction.ResetOverview })
            })
    }

    return {
        selectedFile,
        attachments,
        attachmentFolderMissing: !!selectedFile && attachmentFolderLoaded && !attachmentFolder,
        canUpload: !!attachmentFolder,
        uploadFiles,
        registerKnownItems,
    }
}
